import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import Header from '../../components/Header';
import KpiCard from '../../components/KpiCard';
import { colors, radius, shadows } from '../../styles/theme';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

const LogoSenkronizasyonPage = () => {
  useAuth();

  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [ozet, setOzet] = useState(null);
  const [senkFaturalar, setSenkFaturalar] = useState([]);
  const [senkLoading, setSenkLoading] = useState(false);
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [selectedLokasyon, setSelectedLokasyon] = useState('');

  // Tarih
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [baslangic, setBaslangic] = useState(firstOfMonth.toISOString().split('T')[0]);
  const [bitis, setBitis] = useState(today.toISOString().split('T')[0]);

  // İşlem sonuçları
  const [sonuc, setSonuc] = useState(null);
  const [islemYapiliyor, setIslemYapiliyor] = useState(false);

  // Bağlantı kontrol
  useEffect(() => {
    const check = async () => {
      try {
        const res = await fetch(`${API_URL}/api/logo/status`);
        if (res.ok) setStatus(await res.json());
      } catch {
        setStatus({ connected: false });
      } finally {
        setStatusLoading(false);
      }
    };
    check();
  }, []);

  // Lokasyonlar
  useEffect(() => {
    const fetchLok = async () => {
      try {
        const res = await fetch(`${API_URL}/api/lokasyonlar`, { headers: getAuthHeaders() });
        if (res.ok) {
          const data = await res.json();
          setLokasyonlar(Array.isArray(data) ? data : []);
          // İlk depo lokasyonunu seç
          const depo = data.find(l => l.tip === 'DEPO');
          if (depo) setSelectedLokasyon(String(depo.id));
        }
      } catch (err) {
        logError('Lokasyonlar yüklenemedi:', err);
      }
    };
    fetchLok();
  }, []);

  // Özet
  const fetchOzet = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/logo/senk/ozet`, { headers: getAuthHeaders() });
      if (res.ok) setOzet(await res.json());
    } catch (err) {
      logError('Özet yüklenemedi:', err);
    }
  }, []);

  // Senkronize edilmiş faturalar
  const fetchSenkFaturalar = useCallback(async () => {
    setSenkLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/logo/senk/faturalar`, { headers: getAuthHeaders() });
      if (res.ok) setSenkFaturalar(await res.json());
    } catch (err) {
      logError('Senk faturalar yüklenemedi:', err);
    } finally {
      setSenkLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOzet();
    fetchSenkFaturalar();
  }, [fetchOzet, fetchSenkFaturalar]);

  // Senkronizasyon başlat
  const senkronizeEt = async (tip) => {
    setIslemYapiliyor(true);
    setSonuc(null);
    try {
      const endpoint = tip === 'GELEN' ? 'gelen' : tip === 'GIDEN' ? 'giden' : 'gider';
      const body = {
        fatura_tipi: tip,
        baslangic,
        bitis,
      };
      if (selectedLokasyon) {
        body.lokasyon_id = parseInt(selectedLokasyon);
      }

      const res = await fetch(`${API_URL}/api/logo/senk/${endpoint}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });

      if (res.ok) {
        const data = await res.json();
        setSonuc({ basarili: true, tip, ...data });
        // Listeyi yenile
        fetchOzet();
        fetchSenkFaturalar();
      } else {
        const err = await res.json().catch(() => ({}));
        setSonuc({ basarili: false, tip, message: err.detail || 'Senkronizasyon başarısız' });
      }
    } catch (err) {
      setSonuc({ basarili: false, tip, message: err.message });
    } finally {
      setIslemYapiliyor(false);
    }
  };

  const formatCurrency = (val) => {
    if (!val && val !== 0) return '-';
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(val);
  };

  if (statusLoading) {
    return (
      <div style={{ padding: '32px' }}>
        <Header title="Fatura Senkronizasyonu" subtitle="Logo İşbaşı fatura verilerini ERP'ye aktarın" />
        <div style={{ textAlign: 'center', padding: '60px', color: colors.textSecondary }}>
          ⏳ Bağlantı kontrol ediliyor...
        </div>
      </div>
    );
  }

  if (!status?.connected) {
    return (
      <div style={{ padding: '32px' }}>
        <Header title="Fatura Senkronizasyonu" subtitle="Logo İşbaşı fatura verilerini ERP'ye aktarın" />
        <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: radius.lg, padding: '24px', margin: '24px 0' }}>
          <h3 style={{ margin: '0 0 8px', color: '#856404' }}>⚠️ Logo İşbaşı Bağlantısı Yok</h3>
          <p style={{ margin: 0, color: '#856404' }}>{status?.message || 'API bilgileri yapılandırılmamış.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '32px' }}>
      <Header title="Fatura Senkronizasyonu" subtitle="Logo İşbaşı faturaları → ERP (Cari + Stok + Kasa)" />

      {/* Bağlantı OK */}
      <div style={{ background: '#d4edda', border: '1px solid #28a745', borderRadius: radius.md, padding: '8px 16px', display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '20px', fontSize: '13px', color: '#155724' }}>
        ✅ Logo İşbaşı bağlantısı aktif
      </div>

      {/* KPI */}
      {ozet && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px', marginBottom: '24px' }}>
          <KpiCard label="Toplam Senkronize" value={ozet.toplam_senk} icon="🔄" />
          <KpiCard label="Cari Güncellenen" value={ozet.cari_guncellenen} icon="👥" color="#28a745" />
          <KpiCard label="Stok Güncellenen" value={ozet.stok_guncellenen} icon="📦" color="#1976d2" />
          <KpiCard label="Gider Kaydedilen" value={ozet.gider_kaydedilen || 0} icon="💸" color="#e65100" />
          <KpiCard label="Ödeme Oluşturulan" value={ozet.odeme_olusturulan} icon="💰" color="#6f42c1" />
          <KpiCard label="Eşleşmeyen Cari" value={ozet.eslesmeyen_cari} icon="⚠️" color={ozet.eslesmeyen_cari > 0 ? '#dc3545' : '#28a745'} />
          <KpiCard label="Toplam Tutar" value={formatCurrency(ozet.toplam_tutar)} icon="💵" />
        </div>
      )}

      {/* Senkronizasyon Paneli */}
      <div style={{ background: colors.surface, borderRadius: radius.lg, boxShadow: shadows.sm, padding: '24px', marginBottom: '24px' }}>
        <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 600 }}>🔄 Yeni Senkronizasyon Başlat</h3>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '16px' }}>
          <div>
            <label style={labelStyle}>Başlangıç</label>
            <input type="date" value={baslangic} onChange={e => setBaslangic(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Bitiş</label>
            <input type="date" value={bitis} onChange={e => setBitis(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Stok Lokasyonu</label>
            <select value={selectedLokasyon} onChange={e => setSelectedLokasyon(e.target.value)} style={{ ...inputStyle, minWidth: '200px' }}>
              <option value="">-- Stok güncelleme yapma --</option>
              {lokasyonlar.map(l => (
                <option key={l.id} value={l.id}>{l.ad} ({l.tip})</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            onClick={() => senkronizeEt('GELEN')}
            disabled={islemYapiliyor}
            style={{
              ...btnStyle,
              background: islemYapiliyor ? '#ccc' : '#1976d2',
            }}
          >
            {islemYapiliyor ? '⏳ İşleniyor...' : '📥 Gelen Faturaları Senkronize Et'}
          </button>
          <button
            onClick={() => senkronizeEt('GIDEN')}
            disabled={islemYapiliyor}
            style={{
              ...btnStyle,
              background: islemYapiliyor ? '#ccc' : '#28a745',
            }}
          >
            {islemYapiliyor ? '⏳ İşleniyor...' : '📤 Giden Faturaları Senkronize Et'}
          </button>
          {/* Gider senkronizasyonu devre dışı — giderler ERP'den girilecek */}
        </div>

        {/* Açıklama */}
        <div style={{ marginTop: '16px', padding: '12px 16px', background: colors.backgroundAlt, borderRadius: radius.md, fontSize: '12px', color: colors.textSecondary, lineHeight: '1.6' }}>
          <strong>Senkronizasyon ne yapar?</strong><br />
          ✅ <strong>Cari Güncelleme:</strong> Faturadaki tedarikçi eşleştirilmişse ERP'de fatura kaydı oluşur → cari bakiye otomatik artar<br />
          📦 <strong>Stok Güncelleme:</strong> Lokasyon seçildiyse, fatura kalemlerindeki ürünler eşleştirilmiş malzemelere stok girişi yapar<br />
          💰 <strong>Ödeme Kaydı:</strong> Faturanın kalan tutarı 0 ise (Logo'da ödenmiş), ERP'de ödeme kaydı oluşturulur
        </div>
      </div>

      {/* Sonuç */}
      {sonuc && (
        <div style={{
          background: sonuc.basarili ? '#d4edda' : '#f8d7da',
          border: `1px solid ${sonuc.basarili ? '#28a745' : '#dc3545'}`,
          borderRadius: radius.lg,
          padding: '20px',
          marginBottom: '24px',
        }}>
          <h4 style={{ margin: '0 0 12px', color: sonuc.basarili ? '#155724' : '#721c24' }}>
            {sonuc.basarili ? '✅ Senkronizasyon Tamamlandı' : '❌ Senkronizasyon Başarısız'}
          </h4>
          {sonuc.basarili ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
              <div style={sonucKutuStyle}>
                <div style={{ fontSize: '24px', fontWeight: 700 }}>{sonuc.senkronize || 0}</div>
                <div style={{ fontSize: '12px', color: colors.textSecondary }}>Senkronize Edilen</div>
              </div>
              <div style={sonucKutuStyle}>
                <div style={{ fontSize: '24px', fontWeight: 700 }}>{sonuc.atlanan || 0}</div>
                <div style={{ fontSize: '12px', color: colors.textSecondary }}>Atlanan (zaten mevcut)</div>
              </div>
              {sonuc.stok_guncellenen !== undefined && (
                <div style={sonucKutuStyle}>
                  <div style={{ fontSize: '24px', fontWeight: 700 }}>{sonuc.stok_guncellenen || 0}</div>
                  <div style={{ fontSize: '12px', color: colors.textSecondary }}>Stok Güncellenen</div>
                </div>
              )}
              {sonuc.gider_olusturulan !== undefined && sonuc.gider_olusturulan > 0 && (
                <div style={sonucKutuStyle}>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: '#e65100' }}>{sonuc.gider_olusturulan}</div>
                  <div style={{ fontSize: '12px', color: colors.textSecondary }}>Gider Kaydedilen</div>
                </div>
              )}
              {sonuc.odeme_olusturulan !== undefined && (
                <div style={sonucKutuStyle}>
                  <div style={{ fontSize: '24px', fontWeight: 700 }}>{sonuc.odeme_olusturulan || 0}</div>
                  <div style={{ fontSize: '12px', color: colors.textSecondary }}>Ödeme Oluşturulan</div>
                </div>
              )}
              {sonuc.hatalar && sonuc.hatalar.length > 0 && (
                <div style={{ ...sonucKutuStyle, background: '#fff5f5' }}>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: '#dc3545' }}>{sonuc.hatalar.length}</div>
                  <div style={{ fontSize: '12px', color: '#dc3545' }}>Hata</div>
                </div>
              )}
            </div>
          ) : (
            <p style={{ margin: 0, color: '#721c24' }}>{sonuc.message}</p>
          )}
          {sonuc.hatalar && sonuc.hatalar.length > 0 && (
            <div style={{ marginTop: '12px', padding: '8px 12px', background: '#fff5f5', borderRadius: radius.sm, maxHeight: '120px', overflowY: 'auto' }}>
              {sonuc.hatalar.map((h, i) => (
                <div key={i} style={{ fontSize: '11px', color: '#dc3545', marginBottom: '4px' }}>
                  Fatura {h.fatura}: {h.hata}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Senkronize Edilmiş Fatura Listesi */}
      <div style={{ background: colors.surface, borderRadius: radius.lg, boxShadow: shadows.sm, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>📋 Senkronize Edilmiş Faturalar ({senkFaturalar.length})</h3>
          <button onClick={fetchSenkFaturalar} style={{ padding: '4px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: colors.surface, cursor: 'pointer', fontSize: '12px' }}>
            🔄 Yenile
          </button>
        </div>

        {senkLoading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: colors.textSecondary }}>⏳ Yükleniyor...</div>
        ) : senkFaturalar.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: colors.textTertiary }}>
            Henüz senkronize edilmiş fatura yok. Yukarıdaki butonları kullanarak başlayın.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: colors.backgroundAlt, borderBottom: `2px solid ${colors.border}` }}>
                  <th style={thStyle}>Tip</th>
                  <th style={thStyle}>Fatura No</th>
                  <th style={thStyle}>Tarih</th>
                  <th style={thStyle}>Cari</th>
                  <th style={thStyle}>Toplam</th>
                  <th style={thStyle}>Cari</th>
                  <th style={thStyle}>Stok</th>
                  <th style={thStyle}>Ödeme</th>
                  <th style={thStyle}>Notlar</th>
                </tr>
              </thead>
              <tbody>
                {senkFaturalar.map((s) => (
                  <tr key={s.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={tdStyle}>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: radius.sm,
                        fontSize: '11px',
                        fontWeight: 600,
                        background: s.fatura_tipi === 'GELEN' ? '#e3f2fd' : s.fatura_tipi === 'GIDEN' ? '#e8f5e9' : '#fff3e0',
                        color: s.fatura_tipi === 'GELEN' ? '#1565c0' : s.fatura_tipi === 'GIDEN' ? '#2e7d32' : '#e65100',
                      }}>
                        {s.fatura_tipi === 'GELEN' ? '📥 Gelen' : s.fatura_tipi === 'GIDEN' ? '📤 Giden' : '💸 Gider'}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '12px' }}>{s.logo_fatura_no || '-'}</td>
                    <td style={tdStyle}>{s.fatura_tarihi || '-'}</td>
                    <td style={{ ...tdStyle, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.tedarikci_unvan || s.logo_cari_unvan || '-'}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatCurrency(s.toplam_tutar)}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      {s.cari_guncellendi ? <span style={badgeOk}>✅</span> : <span style={badgeFail}>❌</span>}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      {s.stok_guncellendi ? <span style={badgeOk}>✅</span> : <span style={badgeNa}>—</span>}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      {s.odeme_olusturuldu ? <span style={badgeOk}>✅</span> : <span style={badgeNa}>—</span>}
                    </td>
                    <td style={{ ...tdStyle, fontSize: '11px', color: colors.textSecondary, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.notlar || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Styles
const labelStyle = { display: 'block', fontSize: '11px', fontWeight: 600, color: colors.textSecondary, marginBottom: '4px', textTransform: 'uppercase' };
const inputStyle = { padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.md, fontSize: '13px' };
const btnStyle = { padding: '12px 24px', color: '#fff', border: 'none', borderRadius: radius.md, cursor: 'pointer', fontWeight: 600, fontSize: '14px', transition: 'all 0.2s' };
const thStyle = { padding: '6px 8px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: colors.textSecondary, whiteSpace: 'nowrap' };
const tdStyle = { padding: '10px 12px', fontSize: '13px', color: '#333' };
const sonucKutuStyle = { background: colors.backgroundAlt, borderRadius: radius.md, padding: '8px', textAlign: 'center' };
const badgeOk = { color: '#28a745', fontSize: '14px' };
const badgeFail = { color: '#dc3545', fontSize: '14px' };
const badgeNa = { color: colors.textTertiary, fontSize: '12px' };

export default LogoSenkronizasyonPage;
